import { mostrarAcesso } from "@/components/AccessShare";
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Plus, Search, GraduationCap, Link2, Users } from "lucide-react";
import { toast } from "sonner";
import { NewUserDialog } from "@/components/NewUserDialog";
import { EnrollmentsDialog } from "@/components/EnrollmentsDialog";
import { Pager } from "@/components/Pager";

import { confirmar } from "@/components/ConfirmDialog";
const STATUS = {
  cadastrado: ["Cadastrado", "bg-teal-50 text-teal-700"], primeiro_acesso: ["Aguardando 1º acesso", "bg-amber-50 text-amber-700"],
  importado: ["Importado", "bg-sky-50 text-sky-700"], pendente: ["Pendente", "bg-orange-50 text-orange-700"],
};

/** Professor com permissão de cadastro: cadastra e matricula alunos nos seus cursos/turmas. */
export default function ProfStudents() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [enroll, setEnroll] = useState(null);
  const load = () => api.get("/students").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [q]);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (list || []).filter((s) => !t || s.name?.toLowerCase().includes(t) || s.email?.toLowerCase().includes(t) || (s.reg_code || "").includes(t));
  }, [list, q]);

  const resend = async (s) => {
    if (!(await confirmar(`Gerar um novo código de 1º acesso para ${s.name} e enviar por e-mail?`))) return;
    try {
      const r = await api.post(`/students/${s.id}/resend-invite`);
      mostrarAcesso(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Alunos</h1>
          <p className="text-slate-500 mt-1">Cadastre alunos e matricule nas suas turmas e cursos.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="text-white font-semibold" style={{ background: "#1E9E2A" }} data-testid="prof-new-student"><Plus className="h-4 w-4 mr-1.5" /> Novo aluno</Button>
      </div>
      <div className="relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, e-mail ou ID..." className="pl-9 h-10" />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {list === null ? <div className="p-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div> : (
          <>
            <div className="overflow-x-auto iep-scroll">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
                  <th className="px-4 py-3 hidden lg:table-cell">ID</th><th className="px-3 py-3">Aluno</th><th className="px-3 py-3 hidden md:table-cell">Cidade/UF</th>
                  <th className="px-3 py-3">Situação</th><th className="px-4 py-3 text-right">Opções</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center">
                    <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-green-50 text-[#1E9E2A]"><Users className="h-6 w-6" /></div>
                    <p className="mt-3 text-sm font-semibold text-slate-700">Nenhum aluno encontrado</p>
                    <p className="mt-1 text-xs text-slate-500">Toque em <b>Novo aluno</b> para cadastrar e matricular nas suas turmas.</p>
                  </td></tr>}
                  {rows.slice((page - 1) * 50, page * 50).map((s) => {
                    const [label, cls] = STATUS[s.status] || [s.status, "bg-slate-100 text-slate-600"];
                    return (
                      <tr key={s.id}>
                        <td className="px-4 py-2.5 hidden lg:table-cell font-mono text-xs text-slate-500">{s.reg_code || "—"}</td>
                        <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.email}</div></td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-slate-500">{[s.city, s.state].filter(Boolean).join(" / ") || "—"}</td>
                        <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`}>{label}</span></td>
                        <td className="px-4 py-2.5 text-right">
                          <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild><button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onSelect={() => setEnroll(s)}><GraduationCap className="h-4 w-4 mr-2" /> Matrículas (meus cursos)</DropdownMenuItem>
                              {s.status !== "cadastrado" && <DropdownMenuItem onSelect={() => resend(s)}><Link2 className="h-4 w-4 mr-2" /> Enviar acesso (novo código)</DropdownMenuItem>}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager page={page} perPage={50} total={rows.length} onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          </>
        )}
      </div>
      <NewUserDialog open={open} onOpenChange={setOpen} onCreated={load} studentOnly />
      <EnrollmentsDialog student={enroll} onClose={() => setEnroll(null)} />
    </div>
  );
}
